import React from "react";
import Lottie from "react-lottie-player";
import { Capacity, Perspective, UIState } from "../../../App";
import { CapacitiesHelper } from "../../../helpers/capacities.helper";
import { PerspectivesHelper } from "../../../helpers/perspectives.helper";
import StandardButton, { StandardButtonTheme } from "../../common/button/StandardButton";
import './CharacterPanel.scoped.scss';

interface ComponentProps {
    didClickOnCapacity: (capacity: Capacity) => void;
    didClickOnPerspective: (perspective: Perspective) => void;
    uiState: UIState;
    openedCapacity: Capacity;
    openedPerspective: Perspective;
}

interface ComponentState {
    isPlaying: boolean;
    hasInitialized: boolean;
    hasFinishedAnimating: boolean;
}

class CharacterPanel extends React.Component<ComponentProps, ComponentState> {
    static timeoutVariable?: NodeJS.Timeout;

    constructor(props: ComponentProps) {
        super(props);
        this.state = { isPlaying: true, hasInitialized: false, hasFinishedAnimating: false };
    }

    componentDidMount() {
        setTimeout(() => {
            this.setState({ hasInitialized: true });
            setTimeout(() => {
                this.setState({ hasFinishedAnimating: true });
            }, 3200);
        }, 700);
    }

    shouldComponentUpdate(nextProps: ComponentProps, nextState: ComponentState) {
        if (this.props.openedCapacity !== nextProps.openedCapacity) {
            this.pauseAndRescheduleLottieAnimation();
            return true;
        }
        if (this.props.openedPerspective !== nextProps.openedPerspective) {
            this.pauseAndRescheduleLottieAnimation();
            return true;
        }
        if (this.props.uiState !== nextProps.uiState) {
            this.pauseAndRescheduleLottieAnimation();
            return true;
        }
        if (this.state.isPlaying !== nextState.isPlaying || this.state.hasInitialized !== nextState.hasInitialized ||
            this.state.hasFinishedAnimating !== nextState.hasFinishedAnimating) {
            return true;
        }
        return false;
    }

    pauseAndRescheduleLottieAnimation() {
        if (CharacterPanel.timeoutVariable) {
            clearTimeout(CharacterPanel.timeoutVariable);
        }
        let timer = setTimeout(() => {
            CharacterPanel.timeoutVariable = undefined;
            this.setState({ isPlaying: true });
        }, 800);
        CharacterPanel.timeoutVariable = timer;
        this.setState({ isPlaying: false });
    }

    didClickOnCapacity = (capacity: Capacity) => {
        this.props.didClickOnCapacity(capacity);
    }

    didClickOnPerspective = (perspective: Perspective) => {
        this.props.didClickOnPerspective(perspective);
    }

    render() {
        let capacities: JSX.Element[] = [];
        let capacitiesButtons: JSX.Element[] = [];

        let capacityLottieFile: any;
        let generalCharacterLottieFile: any;
        Object.values(Capacity).forEach(capacity => {
            if (capacity !== Capacity.General) {
                let capacityContent = CapacitiesHelper.capacityContentForCapacity(capacity);
                let capacityElement: JSX.Element =
                    <>
                        <div className={['capacity-BGElem', capacity].join(' ')}></div>
                        <div className={['capacity-item', capacity].join(' ')} onClick={() => { this.didClickOnCapacity(capacity); }} key={capacity}>
                        </div>
                    </>
                capacities.push(capacityElement)
                let button: JSX.Element;
                if (this.props.uiState === UIState.CapacitiesOpened) {
                    button =
                        <div className={['capacity-button', capacity].join(' ')} onClick={() => { this.didClickOnCapacity(capacity); }} key={capacity}>
                            <StandardButton buttonText={capacityContent.name} buttonIconURL="/assets/capacityButtonExpendBlueArrowToRight.svg"
                                buttonIconWidth={7} buttonClickCallback={() => { this.didClickOnCapacity(capacity); }} buttonTheme={StandardButtonTheme.whiteBackground}
                            ></StandardButton>
                        </div>
                } else {
                    button =
                        <div className={['capacity-button', capacity].join(' ')} onClick={() => { this.didClickOnCapacity(capacity); }} key={capacity}>
                            <StandardButton buttonText={capacityContent.name} buttonIconBeforeURL="/assets/capacityButtonExpendBlueArrowToLeft.svg"
                                buttonIconWidth={7} buttonClickCallback={() => { this.didClickOnCapacity(capacity); }} buttonTheme={StandardButtonTheme.whiteBackground}
                            ></StandardButton>
                        </div>
                }
                capacitiesButtons.push(button);
                if (this.props.uiState === UIState.CapacitiesOpened && this.props.openedCapacity === capacity && capacityContent.lottieJSONFileName.length > 0) {
                    capacityLottieFile = require('./animations/' + capacityContent.lottieJSONFileName + '.json');
                }
            } else {
                generalCharacterLottieFile = require('./animations/Anim-Character-LandingPage.json');
            }
        });

        let perspectives: JSX.Element[] = [];
        let perspectiveButtons: JSX.Element[] = [];
        let perspectiveLottieFile: any;
        Object.values(Perspective).forEach(perspective => {
            if (perspective !== Perspective.General) {
                let perspectiveContent = PerspectivesHelper.perspectiveContentForPerspective(perspective);
                let perspectiveElement: JSX.Element =
                    <>
                        <div className={['perspective-BGElem', perspective].join(' ')}></div>
                        <div className={['perspective-item', perspective].join(' ')} key={perspective} onClick={() => { this.didClickOnPerspective(perspective); }}>
                        </div>
                    </>
                perspectives.push(perspectiveElement)
                let button: JSX.Element;
                if (this.props.uiState === UIState.CapacitiesOpened) {
                    button =
                        <div className={['perspective-button', perspective].join(' ')} key={perspective} onClick={() => { this.didClickOnPerspective(perspective); }}>
                            <StandardButton buttonText={perspectiveContent.name} buttonIconBeforeURL="/assets/capacityButtonExpendBlueArrowToLeft.svg"
                                buttonIconWidth={7} buttonClickCallback={() => { this.didClickOnPerspective(perspective); }} buttonTheme={StandardButtonTheme.whiteBackground}
                            ></StandardButton>
                        </div>;
                } else {
                    button =
                        <div className={['perspective-button', perspective].join(' ')} key={perspective} onClick={() => { this.didClickOnPerspective(perspective); }}>
                            <StandardButton buttonText={perspectiveContent.name} buttonIconURL="/assets/capacityButtonExpendBlueArrowToRight.svg"
                                buttonIconWidth={7} buttonClickCallback={() => { this.didClickOnPerspective(perspective); }} buttonTheme={StandardButtonTheme.whiteBackground}
                            ></StandardButton>
                        </div>;
                }
                perspectiveButtons.push(button);
                if (this.props.uiState === UIState.PerspectivesOpened && this.props.openedPerspective === perspective && perspectiveContent.lottieJSONFileName.length > 0) {
                    perspectiveLottieFile = require('./animations/' + perspectiveContent.lottieJSONFileName + '.json');
                }
            } else {
                generalCharacterLottieFile = require('./animations/Anim-Character-LandingPage.json');
            }
        });


        let uiState = "";
        let openedElement = "";
        if (this.props.uiState === UIState.CapacitiesOpened) {
            uiState = "capacitiesOpened";
            openedElement = this.props.openedCapacity;
        } else if (this.props.uiState === UIState.PerspectivesOpened) {
            uiState = "perspectivesOpened";
            openedElement = this.props.openedPerspective;
        } else {
            generalCharacterLottieFile = require('./animations/Anim-Character-LandingPage.json');
        }

        console.log("Render Character Panel");

        const rendererSettings = {
            preserveAspectRatio: 'xMidYMid slice'
        }
        const rendererSettingsFit = {
            preserveAspectRatio: 'xMidYMid meet'
        }

        let capacityLottieElement: JSX.Element = <></>;
        if (capacityLottieFile) {
            capacityLottieElement = <Lottie rendererSettings={rendererSettingsFit} className="illustrated-child" loop animationData={capacityLottieFile} play={this.state.isPlaying} />
        }

        let perspectiveLottieElement: JSX.Element = <></>;
        if (perspectiveLottieFile) {
            perspectiveLottieElement = <div className="lottie-perspective-animation-wrp">
                <div className="lottie-perspective-animation-ratioKeeper">
                    <Lottie rendererSettings={rendererSettings} className="illustrated-child" loop animationData={perspectiveLottieFile} play={this.state.isPlaying} />
                    <img src="/assets/perspectiveImageRatioKeeper.png" className="perspectiveImageRatioKeeper" />
                </div>
            </div>
        }

        let generalLottieElement: JSX.Element = <></>;
        if (generalCharacterLottieFile && (this.props.uiState === UIState.Intro ||
            (this.props.uiState === UIState.CapacitiesOpened && this.props.openedCapacity === Capacity.General) ||
            (this.props.uiState === UIState.PerspectivesOpened && this.props.openedPerspective === Perspective.General))
        ) {
            generalLottieElement = <Lottie className="illustrated-child general" loop animationData={generalCharacterLottieFile} play={this.state.isPlaying} />
        }

        let hasInitialized = this.state.hasInitialized ? "" : "initialState";
        let introAnimationClass = this.state.hasFinishedAnimating ? "" : "introAnimation"

        return (
            <>
                <div className={['capacities-button-wrp', uiState, openedElement].join(' ')}>
                    {capacitiesButtons}
                </div>
                <div className={['character-panel-wrp', uiState, openedElement, hasInitialized, introAnimationClass].join(' ')}>
                    <div className="capacities-wrp">
                        <div className="capacity-BGElem-texture"></div>
                        {capacityLottieElement}
                        {capacities}
                    </div>
                    <div className="generalCharacter">{generalLottieElement}</div>
                    <div className="perspectives-wrp">
                        <div className="capacity-BGElem-texture"></div>
                        {perspectives}
                        {perspectiveLottieElement}
                    </div>
                </div>
                <div className={['perspectives-button-wrp', uiState, openedElement].join(' ')}>
                    {perspectiveButtons}
                </div>
            </>
        )
    }
}

export default CharacterPanel;